import type { FC } from "react";
import React, { useState } from "react";
import { useSite } from "@edreamsodigeo/frontend-core";

import {
  Perk,
  Plan,
  type LightboxBenefit,
} from "~/clientState/hooks/model/lighboxContract";
import type {
  AccordionContent,
  AccordionItemProps,
  CategorySectionProps,
  ExtraContent,
} from "~/components/acquisition/Lightbox/LightboxRevamp/components/LightboxBenefits/types";
import insertElementsIntoBenefits from "./insertElementsIntoBenefits";
import useLightboxRevampData from "~/clientState/hooks/useLightboxRevampData";
import { isFreeCancellationRolloutMarket } from "~/clientState/properties/FreeCancellationRolloutSiteProperties";
import useGetBenefitFromOffer from "~/clientState/hooks/useGetBenefitFromOffer";
import {
  MembershipPerkType,
  SubscriptionOfferPeriod,
  SubscriptionOfferType,
} from "~/generated/graphql";
import useIsLoyaltyRewardOnPrimeFirst from "~/clientState/hooks/useIsLoyaltyRewardOnPrimeFirst";
import useHasLoyaltyProgram from "~/clientState/hooks/useHasLoyaltyProgram";

interface GroupByCategoryArgs {
  showPrimePlus: boolean;
  showFreeCancellation: boolean;
  showFreeCancellationFreeTrial: boolean;
  showPrimeRewards: boolean;
  benefits?: LightboxBenefit[];
  AccordionItem: FC<AccordionItemProps>;
  openIndex: string | null;
  handleItemClick: (title: string) => void;
  extraElements?: ExtraContent[];
}

const getFreeCancellationBenefitToShow = (
  showFreeCancellationFreeTrial: boolean,
): Perk => {
  return showFreeCancellationFreeTrial
    ? Perk.FreeCancellationFreeTrial
    : Perk.FreeCancellation;
};

const isPrimePlusBenefitVisible = (
  item: LightboxBenefit,
  showPrimePlus: boolean,
): boolean => {
  return (
    showPrimePlus &&
    item.plan === Plan.PrimePlus &&
    item.perk !== Perk.FreeCancellation &&
    item.perk !== Perk.FreeCancellationFreeTrial
  );
};

const isFreeCancellationBenefitVisible = (
  item: LightboxBenefit,
  showFreeCancellation: boolean,
  showFreeCancellationFreeTrial: boolean,
  freeCancellationBenefitToShow: Perk,
): boolean => {
  return (
    (showFreeCancellation || showFreeCancellationFreeTrial) &&
    item.perk === freeCancellationBenefitToShow
  );
};

const groupByCategory = ({
  showPrimePlus,
  showFreeCancellation,
  showFreeCancellationFreeTrial,
  showPrimeRewards,
  benefits,
  AccordionItem,
  openIndex,
  handleItemClick,
  extraElements = [],
}: GroupByCategoryArgs): AccordionContent[] => {
  const groupedElements: { [key: string]: React.JSX.Element[] } = {};

  const freeCancellationBenefitToShow = getFreeCancellationBenefitToShow(
    showFreeCancellationFreeTrial,
  );

  benefits?.forEach((item) => {
    if (!groupedElements[item.category]) {
      groupedElements[item.category] = [];
    }

    const showPrimePlusBenefit = isPrimePlusBenefitVisible(item, showPrimePlus);
    const showFreeCancellationBenefit =
      isFreeCancellationBenefitVisible(
        item,
        showFreeCancellation,
        showFreeCancellationFreeTrial,
        freeCancellationBenefitToShow,
      ) && showPrimePlus;

    if (
      (item.isPrime3 ? showPrimeRewards : item.plan === Plan.Prime) ||
      showPrimePlusBenefit ||
      showFreeCancellationBenefit
    ) {
      groupedElements[item.category].push(
        <AccordionItem
          key={`accordion-item-${item.title}`}
          item={item}
          openIndex={openIndex}
          handleItemClick={handleItemClick}
        />,
      );
    }
  });

  const groupedBenefits = Object.keys(groupedElements).map((category) => ({
    category,
    elements: groupedElements[category],
  }));

  return insertElementsIntoBenefits(groupedBenefits, extraElements);
};

export const createHandleItemClick =
  (openIndex, setOpenIndex) =>
  (index: string): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

interface BenefitsByCategoryProps {
  showPrimePlus?: boolean;
  AccordionItem: FC<AccordionItemProps>;
  CategorySection: FC<CategorySectionProps>;
  extraElements?: ExtraContent[];
}

const BenefitsByCategory: FC<BenefitsByCategoryProps> = ({
  showPrimePlus = true,
  AccordionItem,
  CategorySection,
  extraElements,
}) => {
  const { data } = useLightboxRevampData();
  const site: string = useSite();
  const showFreeCancellation = isFreeCancellationRolloutMarket(site);
  const { isBenefitIncluded: showFreeCancellationFreeTrial } =
    useGetBenefitFromOffer({
      offerPeriod: SubscriptionOfferPeriod.FreeTrial,
      offerType: SubscriptionOfferType.Plus,
      perkType: MembershipPerkType.ItineraryCancellationForSubscriptionPurchase,
    });

  const { isEligibleForLoyaltyProgram, userAccountData } =
    useHasLoyaltyProgram();
  const { isLoyaltyRewardProgramIncluded } = useIsLoyaltyRewardOnPrimeFirst();

  const showPrimeRewards = userAccountData?.userAccount?.isLoggedIn
    ? isEligibleForLoyaltyProgram
    : isLoyaltyRewardProgramIncluded;

  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const handleItemClick = createHandleItemClick(openIndex, setOpenIndex);

  const benefits = data?.benefits;

  const groupedBenefits = groupByCategory({
    showPrimePlus,
    showFreeCancellation,
    showFreeCancellationFreeTrial,
    showPrimeRewards,
    benefits,
    AccordionItem,
    openIndex,
    handleItemClick,
    extraElements,
  });

  return (
    <>
      {groupedBenefits.map((benefit) => {
        return (
          <CategorySection
            key={`benefits-${benefit.category}`}
            category={benefit.category}
          >
            {benefit.elements}
          </CategorySection>
        );
      })}
    </>
  );
};

export default BenefitsByCategory;
